import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook personalizado para gestionar el scroll hacia la parte superior de la página
 * Esta versión está optimizada para forzar el scroll en cualquier navegador
 * @param options - Opciones de configuración para el comportamiento del scroll
 * @returns Una función que se puede llamar para activar manualmente el scroll
 */
const useScrollToTop = (
  options: {
    scrollOnMount?: boolean /** Si debe hacer scroll automáticamente al montar el componente */;
    scrollOnLoad?: boolean /** Si debe hacer scroll cuando la página carga completamente */;
    delay?: number /** Retraso adicional para el scroll después del montaje (en ms) */;
  } = {},
) => {
  /** Valores por defecto */
  const { scrollOnMount = true, scrollOnLoad = true, delay = 100 } = options;

  /** Ref para mantener track de si el componente está montado */
  const isMountedRef = useRef(false);

  /**
   * Implementación forzada y agresiva del scroll hacia arriba
   * Combina todas las técnicas conocidas para garantizar que funcione en cualquier navegador
   */
  const scrollToTop = useCallback(() => {
    /** Técnica 1: Manipular directamente los estilos de scroll */
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;

    /** Guardar valores originales */
    const originalHtmlScrollBehavior = htmlStyle.scrollBehavior;
    const originalBodyScrollBehavior = bodyStyle.scrollBehavior;

    /** Forzar comportamiento inmediato */
    htmlStyle.scrollBehavior = 'auto';
    bodyStyle.scrollBehavior = 'auto';

    try {
      /** Técnica 2: window.scrollTo simple */
      window.scrollTo(0, 0);

      /** Técnica 3: window.scrollTo con opciones */
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });

      /** Técnica 4: Asignación directa de scrollTop */
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      /** Técnica 5: Usar scrollIntoView en el primer elemento */
      if (document.body.firstElementChild) {
        document.body.firstElementChild.scrollIntoView({ block: 'start', behavior: 'auto' });
      }

      /** Técnica 6: Forzar reflow/repaint */
      document.documentElement.getBoundingClientRect();
    } finally {
      /** Restaurar comportamiento original */
      htmlStyle.scrollBehavior = originalHtmlScrollBehavior;
      bodyStyle.scrollBehavior = originalBodyScrollBehavior;
    }
  }, []);

  /** Aplicar scroll con múltiples intentos para asegurar efectividad */
  const forceScrollToTop = useCallback(() => {
    /** Aplicar inmediatamente */
    scrollToTop();

    /** Y luego con retrasos crecientes para asegurar que funcione incluso con contenido dinámico */
    const delays = [50, 100, 200, 500, 1000];
    delays.forEach((ms) => {
      setTimeout(scrollToTop, ms);
    });
  }, [scrollToTop]);

  /** Efecto para realizar scroll automáticamente al montar */
  useEffect(() => {
    /** Marcar como montado */
    isMountedRef.current = true;

    if (scrollOnMount) {
      /** Forzar scroll con múltiples intentos */
      forceScrollToTop();
    }

    /** Manejar evento load */
    const handleLoad = () => {
      /** Solo ejecutar si el componente sigue montado */
      if (isMountedRef.current) {
        forceScrollToTop();
      }
    };

    if (scrollOnLoad) {
      /** Añadir listener para el evento load */
      window.addEventListener('load', handleLoad);

      /** También intentar con DOMContentLoaded para mayor cobertura */
      if (document.readyState !== 'complete' && document.readyState !== 'interactive') {
        window.addEventListener('DOMContentLoaded', handleLoad);
      }
    }

    return () => {
      /** Marcar como desmontado */
      isMountedRef.current = false;

      /** Limpiar todos los event listeners */
      if (scrollOnLoad) {
        window.removeEventListener('load', handleLoad);
        window.removeEventListener('DOMContentLoaded', handleLoad);
      }
    };
  }, [scrollOnMount, scrollOnLoad, forceScrollToTop]);

  /** Retornar la función agresiva de scroll para uso manual */
  return forceScrollToTop;
};

export default useScrollToTop;
